/** @format */

import {
  CRITEO_DIGITAL_SHELF_TALKER_PINNED_POSITION,
  CRITEO_PINNED_CONTENT_REQUIRED_ATTRIBUTES,
  EMPTY_CRITEO_TRACK_TRANSACTION_RESPONSE,
  EMPTY_CRITEO_VIEW_ITEM_RESPONSE,
  EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE,
} from './criteo.constants';
import {
  CertonaSchemeItem,
  Chain,
  CriteoCarouselPlacement,
  CriteoDigitalShelfTalker,
  CriteoDigitalShelfTalkerRendering,
  CriteoHoneycombErrorLog,
  CriteoPinnedContentSpot,
  CriteoPlacement,
  CriteoProduct,
  CriteoTrackTransactionResponse,
  CriteoTransactionMetadata,
  CriteoVideo,
  CriteoViewItemMetadata,
  CriteoViewItemResponse,
  CriteoViewProductsMetadata,
  CriteoViewProductsResponse,
  Environment,
  PlpInGridSponsoredProduct,
  ProductSku,
  ProductTypes,
  PlpDisplayableProduct,
  CriteoViewProductsCriteoResponse,
} from '@shared/data-access/interfaces';
import { DestroyRef, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, fromEvent, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

import { FallbackSeoUrlService } from '@shared/utils/services';
import { Logger } from '@shared/data-access/services';
import { TracerService, type TraceErrorAttributes } from '@shared/ssr-core/data-access';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class CriteoService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fallbackSeoUrlService = inject(FallbackSeoUrlService);
  private readonly chain: Chain = inject(Chain);

  private readonly CRITEO_API_URL: string = '';
  lastInGridOnLoadBeacon = ''; // This is used for the inGrid onLoadBeacons. This is to prevent two criteo calls to be made
  lastDSTOnLoadBeacon = '';
  private readonly observerActiveTimerIds: Set<number> = new Set();

  // PLP/SRLP Global variables
  everyThirdSlot = 0;
  pinnedContentInFirstRow = 0;
  sponsoredShelfPositions: number[] = [4, 5, 6, 7];
  shelfChecked = false;
  // Performance optimizations: Cache frequently used values
  private authStateCacheTime = 0;
  private cachedAuthState: string | null = null;
  private readonly AUTH_CACHE_DURATION = 30000; // 30 seconds
  private readonly cachedDefaultParams: Map<string, HttpParams> = new Map();

  // Pre-compiled regex patterns for better performance
  private readonly COLOR_QUOTE_REGEX = /"([^"]+)"/g;
  // a leading non-letter/start keeps this from matching "Color:[" inside "SaleColor:["
  private readonly COLOR_REGEX = /(?:^|[^A-Za-z])Color:\[(.*?)\]/;
  private readonly SALE_COLOR_REGEX = /SaleColor:\[(.*?)\]/;
  private readonly SALE_CATEGORY_REGEX = /\b(sale|deal|clearance)s?\b/i;
  private readonly PRICE_REGEX = /Price:\[(.*?)\]/;
  private readonly BADGE_REGEX = /Badge:"([^"]+)"/;
  private readonly MAP_PRICE_INDICATOR_REGEX = /mapPriceIndicator:\s*["']?(\d+)["']?/;
  private readonly defaultPLPProductTemplate = {
    Color: [''],
    CustomerRating: '',
    ID: '',
    ImageURL: '',
    Name: '',
    mapPriceIndicator: 0,
    maxlistprice: '',
    maxmapprice: '',
    maxofferprice: '',
    minlistprice: '',
    minmapprice: '',
    minofferprice: '',
    offerprice: '',
    onlineInventoryMessage: '',
  };

  private readonly defaultCertonaTemplate = {
    click_tracker: '',
    impression_tracker: '',
    BreakingMap: '',
    CustomerRating: '',
    ID: '',
    ImageURL: '',
    Name: '',
    ReviewCount: '',
    SKUID: '',
    URL: '',
    WCSIdentifier: '',
    isSkuRecommendation: false,
    mapPriceIndicator: 0,
    maxlistprice: '',
    maxmapprice: '',
    maxofferprice: '',
    minlistprice: '',
    minmapprice: '',
    minofferprice: '',
    offerprice: '',
  };

  private readonly CAROUSEL_KEYS = new Set([
    'viewSearchResult_API_desktop-Carousel',
    'viewSearchResult_API_mobile-Carousel',
    'viewCategory_API_desktop-Carousel',
    'viewCategory_API_mobile-Carousel',
    'trackTransaction_API_desktop-Carousel',
    'trackTransaction_API_mobile-Carousel',
  ]);

  private readonly INGRID_KEYS = new Set([
    'viewSearchResult_API_desktop-InGrid',
    'viewSearchResult_API_mobile-InGrid',
    'viewCategory_API_desktop-InGrid',
    'viewCategory_API_mobile-InGrid',
  ]);

  private readonly DST_KEYS = new Set([
    'viewSearchResult_API_desktop-Digital Shelf Talker',
    'viewSearchResult_API_mobile-Digital Shelf Talker',
    'viewCategory_API_desktop-Digital Shelf Talker',
    'viewCategory_API_mobile-Digital Shelf Talker',
  ]);

  private readonly VIDEO_KEYS = new Set([
    'viewCategory_API_desktop-Video',
    'viewCategory_API_mobile-Video',
    'viewSearchResult_API_desktop-Video',
    'viewSearchResult_API_mobile-Video',
  ]);

  private readonly environment = inject(Environment);
  private readonly http = inject(HttpClient);
  private readonly logger = inject(Logger);
  private readonly tracer = inject(TracerService);

  constructor() {
    this.CRITEO_API_URL = this.environment.api.criteo.baseUrl;
    // Handles clearing remaining timers when navigating between and within apps
    if (isPlatformBrowser(this.platformId)) {
      fromEvent(window, 'beforeunload')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.clearObserverTimers();
        });
    }
  }

  public parseFilters(filters: Record<string, string[]>): string {
    const filterEntries = Object.entries(filters);

    if (filterEntries.length === 0) {
      return '';
    }

    const filterStrings = filterEntries.map(
      ([key, values]) => `(${key},${this.formatFilterValues(values)})`,
    );

    return filterStrings.join(',');
  }

  private formatFilterValues(values: string[]): string {
    const n = values.length;

    // Check if values array is empty
    if (n === 0) {
      return '';
    }
    // Pre-allocate array size for better performance
    const formattedValues = new Array(n);

    for (let i = 0; i < n; i++) {
      const value = values[i];
      if (value.startsWith('*-')) {
        formattedValues[i] = `Under ${value.slice(2)}`;
      } else if (value.endsWith('-*')) {
        formattedValues[i] = `Over ${value.slice(0, -2)}`;
      } else {
        formattedValues[i] = value;
      }
    }

    return formattedValues.length === 1
      ? `eq,${formattedValues[0]}`
      : `in,${formattedValues.join(',')}`;
  }

  public transformToCriteoBreadcrumbFormat(input: string): string {
    const parts = input.split('_');
    return parts.slice(1).join('>');
  }

  private getCachedAuthState(): string | null {
    const now = Date.now();
    if (this.cachedAuthState && now - this.authStateCacheTime <= this.AUTH_CACHE_DURATION) {
      return this.cachedAuthState;
    }

    // Read synchronously from localStorage
    const newAuthState = window.localStorage.getItem('TAG_AuthState');

    // If auth state changed, clear the params cache to prevent stale data
    if (this.cachedAuthState !== newAuthState) {
      this.cachedDefaultParams.clear();
    }

    this.cachedAuthState = newAuthState;
    this.authStateCacheTime = Date.now();
    return this.cachedAuthState;
  }

  private getDefaultParams(eventType: string): HttpParams | null {
    const isLoggedIn = this.getCachedAuthState() === 'true';

    // Create cache key that includes auth state to prevent stale data
    const cacheKey = `${eventType}_${isLoggedIn ? 'authenticated' : 'anonymous'}`;
    const cachedParams = this.cachedDefaultParams.get(cacheKey);
    if (cachedParams) {
      return cachedParams;
    }

    const experience = globalThis._dsgTag?.experience;
    const adobeMCVID = globalThis._dsgTag?.CustomerIDs?.AdobeMCVID;

    if (!experience || !adobeMCVID) {
      const missingParameters = [
        ...(experience ? [] : ['experience']),
        ...(adobeMCVID ? [] : ['adobeMCVID']),
      ];

      this.logErrorToHoneycomb({
        name: 'Criteo Missing Required Parameters',
        message: "Criteo call couldn't be made due to missing required parameters.",
        method: 'getDefaultParams',
        metadata: {
          eventType,
          missingParameters,
        },
      });
      return null;
    }

    try {
      let params = new HttpParams()
        .set(
          'criteo-partner-id',
          this.environment.api.criteo.partnerId[this.chain.chainIdentifierAbbr],
        )
        .set('retailer-visitor-id', adobeMCVID)
        .set('environment', experience === 'Mobile' ? 'm' : 'd')
        .set('nolog', this.environment.api.criteo.nolog)
        .set('event-type', eventType)
        .set('page-id', `${eventType}_API_${experience.toLowerCase()}`);

      if (isLoggedIn) {
        params = params
          .set('email', window._dsgTag?.CustomerIDs?.HashedEmail)
          .set('customer-id', window._dsgTag?.CustomerIDs?.AthleteID);
      }

      this.cachedDefaultParams.set(cacheKey, params);
      return params;
    } catch (error) {
      this.logErrorToHoneycomb({
        name: 'Criteo Parameter Error',
        message: error?.message || 'Error in getting parameters required for Criteo call',
        method: 'getDefaultParams',
        errorStack: error?.stack,
      });
      throw new Error(
        `Failed to construct Criteo parameters: ${error?.message || 'Unknown error'}`,
      );
    }
  }

  private isOptedOut(): boolean {
    if (window._dsgTag?.DoNotSell) {
      return true;
    }
    return false;
  }

  private handleError(error: any): Observable<CriteoViewProductsResponse> {
    if (error?.name === 'TimeoutError') {
      this.logger.error('Criteo API request timed out');
      this.logErrorToHoneycomb({
        name: 'Criteo API Timeout',
        message: error?.message || 'Criteo API request timed out',
        errorName: error?.name,
        errorStack: error?.stack,
      });
    } else {
      this.logger.error('Criteo call failed: ', error);
      this.logErrorToHoneycomb({
        name: 'Criteo API call Failed',
        message: error?.message || 'Criteo API call failed',
        errorName: error?.name,
        errorStack: error?.stack,
      });
    }
    return of({ ...EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE });
  }

  private logErrorToHoneycomb(error: CriteoHoneycombErrorLog) {
    if (this.environment.killSwitch?.honeycombEnabled?.[this.chain.chainIdentifierAbbr]) {
      const attributes: TraceErrorAttributes = {
        name: error.name,
        message: error?.message,
        service: 'CriteoService',
        method: error.method,
        errorStack: error?.errorStack,
      };

      for (const [key, value] of Object.entries(error?.metadata || {})) {
        attributes[`metadata.${key}`] = value;
      }

      this.tracer.logError(attributes);
    }
  }

  // Used for OCP to get and map criteo products
  public trackTransaction(
    transactionMetadata: CriteoTransactionMetadata,
  ): Observable<CriteoTrackTransactionResponse> {
    if (this.isOptedOut()) {
      return of({ ...EMPTY_CRITEO_TRACK_TRANSACTION_RESPONSE });
    }

    const defaultParams = this.getDefaultParams('trackTransaction');
    if (!defaultParams) {
      return of({ ...EMPTY_CRITEO_TRACK_TRANSACTION_RESPONSE });
    }

    let httpParams = defaultParams;
    httpParams = this.addParamIfPresent(httpParams, 'item', transactionMetadata.items); // '123|456|789'
    httpParams = this.addParamIfPresent(httpParams, 'price', transactionMetadata.prices); // '2.99|23.99|54.49'
    httpParams = this.addParamIfPresent(httpParams, 'quantity', transactionMetadata.quantities); // '1|4|1'
    httpParams = this.addParamIfPresent(
      httpParams,
      'transaction-id',
      transactionMetadata.transactionId,
    );
    httpParams = this.addParamIfPresent(httpParams, 'currency', transactionMetadata.currency);

    return this.http
      .get<CriteoTrackTransactionResponse>(this.CRITEO_API_URL, { params: httpParams })
      .pipe(
        map((response: CriteoTrackTransactionResponse) => {
          const carousel = [];

          for (const placement of response.placements) {
            const [key, value] = Object.entries(placement)[0];
            if (this.CAROUSEL_KEYS.has(key)) {
              carousel.push(value[0]);
            }
          }
          if (carousel.length === 0) {
            carousel.push({ products: [] } as CriteoPlacement);
          }
          const carouselPlacement: CriteoCarouselPlacement = { carousel };
          const res: CriteoTrackTransactionResponse = {
            OnLoadBeacon: response.OnLoadBeacon,
            placements: [carouselPlacement],
          };
          return res;
        }),
        catchError(this.handleError.bind(this)),
      );
  }

  public viewItem(viewItemMetadata: CriteoViewItemMetadata): Observable<CriteoViewItemResponse> {
    if (this.isOptedOut()) {
      return of({ ...EMPTY_CRITEO_VIEW_ITEM_RESPONSE });
    }

    const defaultParams = this.getDefaultParams('viewItem');
    if (!defaultParams) {
      return of({ ...EMPTY_CRITEO_VIEW_ITEM_RESPONSE });
    }

    let httpParams = defaultParams;
    httpParams = this.addParamIfPresent(httpParams, 'item', viewItemMetadata.item);
    httpParams = this.addParamIfPresent(httpParams, 'parent-item', viewItemMetadata.parentItem);
    httpParams = this.addParamIfPresent(httpParams, 'category', viewItemMetadata.category);
    httpParams = this.addParamIfPresent(httpParams, 'price', viewItemMetadata.price);
    httpParams = this.addParamIfPresent(httpParams, 'availability', viewItemMetadata.availability);

    return this.http.get<CriteoViewItemResponse>(this.CRITEO_API_URL, { params: httpParams }).pipe(
      timeout(1000),
      map((response: CriteoViewItemResponse) => {
        const placements = response.placements.map((placement) => {
          const carousel = Object.values(placement).flat();
          return { carousel };
        });

        if (placements.length === 0) {
          placements.push({ carousel: [{ products: [] } as CriteoPlacement] });
        }

        return {
          OnLoadBeacon: response.OnLoadBeacon,
          placements,
        };
      }),
      catchError(this.handleError.bind(this)),
    );
  }

  /**
   * Adds a parameter to the HttpParams object if the value is not null, undefined, or an empty string.
   *
   * @param params The original HttpParams object to which the parameter will be added.
   * @param key The key of the parameter to be added.
   * @param value The value of the parameter. It can be a string, number, or boolean. Expected format: '123|456|789'
   * If the value is null, undefined, or an empty string, the parameter will not be added.
   * @return A new HttpParams object with the parameter added, or the original object if the value was not present.
   */
  private addParamIfPresent(
    params: HttpParams,
    key: string,
    value: string | number | boolean | undefined,
  ): HttpParams {
    return value !== null && value !== undefined && value !== ''
      ? params.set(key, value.toString())
      : params;
  }

  private buildEmptyVideoPlacement(): CriteoVideo {
    return {
      format: '',
      LineItemID: '',
      OnBundleBasketChangeBeacon: '',
      OnClickBeacon: '',
      OnFileClickBeacon: '',
      OnLoadBeacon: '',
      OnViewBeacon: '',
      TagVideoVAST: '',
      products: [],
      products_order: [],
      rendering: {
        background_video_alt_text: '',
        color_mode: '',
        optional_redirect_target: '',
        optional_legal_text: '',
      },
    };
  }

  private extractPlacementsFromArray(placements: unknown[]): {
    carousel: CriteoPlacement[];
    inGrid: CriteoPlacement[];
    digitalShelfTalker?: CriteoDigitalShelfTalker;
    videos: CriteoVideo[];
  } {
    const carousel: CriteoPlacement[] = [];
    const inGrid: CriteoPlacement[] = [];
    let digitalShelfTalker: CriteoDigitalShelfTalker | undefined;
    const videos: CriteoVideo[] = [];

    for (const placement of placements) {
      const [key, value] = Object.entries(placement as Record<string, unknown>)[0] as [
        string,
        CriteoPlacement[] | CriteoDigitalShelfTalker[] | CriteoVideo[],
      ];
      if (this.CAROUSEL_KEYS.has(key)) {
        carousel.push((value as CriteoPlacement[])[0]);
      } else if (this.INGRID_KEYS.has(key)) {
        inGrid.push((value as CriteoPlacement[])[0]);
      } else if (this.DST_KEYS.has(key)) {
        digitalShelfTalker = (value as CriteoDigitalShelfTalker[])[0];
      } else if (this.VIDEO_KEYS.has(key)) {
        videos.push((value as CriteoVideo[])[0]);
      }
    }

    return { carousel, inGrid, digitalShelfTalker, videos };
  }

  public viewProductsPage(
    viewProductsMetadata: CriteoViewProductsMetadata,
  ): Observable<CriteoViewProductsResponse> {
    if (this.isOptedOut()) {
      return of({ ...EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE });
    }

    const eventType =
      ProductTypes.VIEW_CATEGORY === viewProductsMetadata.productType
        ? 'viewCategory'
        : 'viewSearchResult';
    const defaultParams = this.getDefaultParams(eventType);
    if (!defaultParams) {
      return of({ ...EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE });
    }

    let httpParams = defaultParams;
    httpParams = this.addParamIfPresent(httpParams, 'list-size', viewProductsMetadata.listSize);
    httpParams = this.addParamIfPresent(
      httpParams,
      'page-number',
      viewProductsMetadata.pageNumber + 1, // Criteo API is 1-indexed
    );
    httpParams = this.addParamIfPresent(httpParams, 'filters', viewProductsMetadata.filters);
    httpParams = this.addParamIfPresent(httpParams, 'item', viewProductsMetadata.item);
    httpParams = this.addParamIfPresent(httpParams, 'parent-item', viewProductsMetadata.parentItem);

    if (viewProductsMetadata.productType === ProductTypes.VIEW_CATEGORY) {
      httpParams = this.addParamIfPresent(
        httpParams,
        'category',
        this.getCategoryParameter(viewProductsMetadata.category),
      );
    } else {
      httpParams = this.addParamIfPresent(httpParams, 'keywords', viewProductsMetadata.category);
    }

    return this.http
      .get<CriteoViewProductsCriteoResponse>(this.CRITEO_API_URL, { params: httpParams })
      .pipe(
        map((response: CriteoViewProductsCriteoResponse) => {
          if (
            response.status?.toLowerCase() === 'error' ||
            (response.errors && response.errors.length > 0)
          ) {
            this.logErrorToHoneycomb({
              name: 'Criteo API Error',
              message: response.errors?.join(', ') || 'Criteo API returned an error',
              method: 'viewProductsPage',
            });
          }
          const { carousel, inGrid, digitalShelfTalker, videos } = this.extractPlacementsFromArray(
            response.placements,
          );

          if (carousel.length === 0) {
            carousel.push({ products: [] } as CriteoPlacement);
          }
          if (inGrid.length === 0) {
            inGrid.push({ products: [] } as CriteoPlacement);
          }
          if (videos.length === 0) {
            videos.push(this.buildEmptyVideoPlacement());
          }

          const res: CriteoViewProductsResponse = {
            OnLoadBeacon: response.OnLoadBeacon,
            placements: {
              carousel,
              inGrid,
              ...(digitalShelfTalker ? { digitalShelfTalker } : {}),
              videos,
            },
          };

          return res;
        }),
        catchError(this.handleError.bind(this)),
      );
  }

  /*
		@param
			beaconUrl - Expect unmodified url from criteo
			excluded - optional, expects productIds separated by pipe
				Ex. 123|456|098
	*/
  public fireBeacon(beaconUrl: string, excluded?: string): void {
    if (!beaconUrl) return;

    const finalUrl = excluded ? `${beaconUrl}&ex=${excluded}` : beaconUrl;

    if (this.environment.killSwitch?.honeycombEnabled?.[this.chain.chainIdentifierAbbr]) {
      this.tracer.log('Criteo beacon fired', {
        message: `__ Criteo beacon fired`,
        service: 'CriteoService',
        method: 'fireBeacon',
        beaconUrl: finalUrl,
      });
    }

    if (navigator.sendBeacon) {
      navigator.sendBeacon(finalUrl);
      return;
    }

    // If sendBeacon is not available, use Image to fire beacon.
    const img = new Image();
    img.src = finalUrl;
  }

  public transformCriteoInGridProducts(sponsoredProducts: CriteoProduct[], isSalesPage = false) {
    return this.transformCriteoProducts(
      sponsoredProducts,
      (product) => this.getFormattedProductForPLP(product),
      isSalesPage,
    );
  }

  public transformCriteoCarouselProducts(sponsoredProducts: CriteoProduct[]) {
    return this.transformCriteoProducts(
      sponsoredProducts,
      (product) => this.getFormattedProductForCarousel(product),
      false,
    );
  }

  private transformCriteoProducts<T extends CertonaSchemeItem | PlpInGridSponsoredProduct>(
    sponsoredProducts: CriteoProduct[],
    formatter: (product: CriteoProduct) => T,
    isSalesPage: boolean,
  ) {
    return sponsoredProducts.map((product) => {
      const formattedProduct = formatter(product);

      this.processProductRenderingAttributes(product, formattedProduct, isSalesPage);

      return formattedProduct;
    });
  }

  private getCategoryParameter(category: string): string {
    if (
      !this.environment.killSwitch?.isEndNodeOnlyEnabled?.[this.chain.chainIdentifierAbbr] ||
      !category.includes('>')
    ) {
      return category;
    }
    return category.substring(category.lastIndexOf('>') + 1);
  }

  private processProductRenderingAttributes(
    product: CriteoProduct,
    formattedProduct: CertonaSchemeItem | PlpInGridSponsoredProduct,
    isSalesPage: boolean,
  ): void {
    try {
      const renderingAttr = JSON.parse(product.RenderingAttributes);
      const renderingAttributesString = renderingAttr.rendering_attributes;
      const priceMatch = renderingAttributesString.match(this.PRICE_REGEX);

      if (priceMatch) {
        if ('product' in formattedProduct) {
          this.updatePriceData(priceMatch[1], formattedProduct.product);
          this.extractAndSetMapPriceIndicator(renderingAttributesString, formattedProduct);

          this.extractAndSetColors(renderingAttributesString, formattedProduct);
          this.extractAndSetSaleColors(renderingAttributesString, formattedProduct);
          this.applySaleColorRules(formattedProduct, isSalesPage);
          this.extractAndSetBadge(renderingAttributesString, formattedProduct);
          formattedProduct.product.ReviewCount =
            renderingAttr.numberOfReviews === '0' ? '' : renderingAttr.numberOfReviews;
        } else {
          this.updatePriceData(priceMatch[1], formattedProduct);
          this.extractAndSetMapPriceIndicator(renderingAttributesString, formattedProduct);
        }
      }
    } catch (error) {
      this.logger.error('Error parsing rendering attributes: ', {
        error,
        renderingAttr: product.RenderingAttributes,
      });
      this.logErrorToHoneycomb({
        name: 'Error in parsing Criteo Product Rendering Attributes',
        message: error?.message || 'Error in parsing Criteo Product Rendering Attributes',
        method: 'processProductRenderingAttributes',
        errorStack: error?.stack,
        metadata: {
          renderingAttributes: product.RenderingAttributes,
        },
      });
    }
  }

  private updatePriceData(
    priceMatchString: string,
    productObject: CertonaSchemeItem | PlpDisplayableProduct,
  ): void {
    const jsonString = `{${priceMatchString.replace(/'/g, '"')}}`;
    const priceObject = JSON.parse(jsonString);

    productObject.maxlistprice = priceObject?.maxlistprice ?? productObject.maxlistprice;
    productObject.maxmapprice = priceObject?.maxmapprice ?? productObject.maxmapprice;
    productObject.maxofferprice = priceObject?.maxofferprice ?? productObject.maxofferprice;
    productObject.minlistprice = priceObject?.minlistprice ?? productObject.minlistprice;
    productObject.minmapprice = priceObject?.minmapprice ?? productObject.minmapprice;
    productObject.minofferprice = priceObject?.minofferprice ?? productObject.minofferprice;
  }

  private extractAndSetColors(
    renderingAttributesString: string,
    formattedProduct: PlpInGridSponsoredProduct,
  ): void {
    const colorMatch = this.COLOR_REGEX.exec(renderingAttributesString);
    if (colorMatch) {
      const colors: string[] = [];
      let match: RegExpExecArray | null;
      while ((match = this.COLOR_QUOTE_REGEX.exec(colorMatch[1])) !== null) {
        colors.push(match[1]);
      }
      this.COLOR_QUOTE_REGEX.lastIndex = 0; // Reset regex state for future calls
      formattedProduct.product.Color = colors;
    }
  }

  private extractAndSetSaleColors(
    renderingAttributesString: string,
    formattedProduct: PlpInGridSponsoredProduct,
  ): void {
    const saleColorMatch = this.SALE_COLOR_REGEX.exec(renderingAttributesString);
    if (saleColorMatch) {
      const saleColors: string[] = [];
      let match: RegExpExecArray | null;
      while ((match = this.COLOR_QUOTE_REGEX.exec(saleColorMatch[1])) !== null) {
        saleColors.push(match[1]);
      }
      this.COLOR_QUOTE_REGEX.lastIndex = 0; // Reset regex state for future calls
      formattedProduct.product.SaleColor = saleColors;
    }
  }

  /**
   * Returns true when the given value (a product's `taxonomy_text` or the page's
   * seoUrl) contains a sale keyword ('sale', 'deal', or 'clearance').
   */
  public isSaleCategory(taxonomyText?: string): boolean {
    return !!taxonomyText && this.SALE_CATEGORY_REGEX.test(taxonomyText);
  }

  /**
   * On sale/deal/clearance pages, products are restricted to their `SaleColor`
   * colorways (or flagged for exclusion when they have none). If a product
   * has no colors, e.g. golf clubs, they will always display.
   * Other pages are unaffected.
   */
  private applySaleColorRules(
    formattedProduct: PlpInGridSponsoredProduct,
    isSalesPage: boolean,
  ): void {
    if (!isSalesPage) {
      return;
    }

    const colors = formattedProduct.product.Color ?? [];
    const hasColors = colors.some((color) => color.trim().length > 0);
    if (!hasColors) {
      return;
    }

    const saleColors = formattedProduct.product.SaleColor ?? [];
    if (saleColors.length > 0) {
      formattedProduct.product.Color = saleColors;
    } else {
      formattedProduct.excludeForSale = true;
    }
  }

  private extractAndSetBadge(
    renderingAttributesString: string,
    formattedProduct: PlpInGridSponsoredProduct,
  ): void {
    const badgeMatch = this.BADGE_REGEX.exec(renderingAttributesString);
    if (badgeMatch) {
      formattedProduct.product.badge = badgeMatch[1];
    }
  }

  private extractAndSetMapPriceIndicator(
    renderingAttributesString: string,
    formattedProduct: CertonaSchemeItem | PlpInGridSponsoredProduct,
  ): void {
    const mapPriceIndicatorMatch = this.MAP_PRICE_INDICATOR_REGEX.exec(renderingAttributesString);
    if (!mapPriceIndicatorMatch) {
      return;
    }

    const mapPriceIndicator = Number(mapPriceIndicatorMatch[1]);

    if ('product' in formattedProduct) {
      formattedProduct.product.mapPriceIndicator = mapPriceIndicator;
    } else {
      formattedProduct.mapPriceIndicator = mapPriceIndicator;
      if (mapPriceIndicator === 1) {
        formattedProduct.breakingMap = true;
      }
    }
  }

  private getFormattedProductForPLP(product: CriteoProduct): PlpInGridSponsoredProduct {
    return {
      click_tracker: product.OnClickBeacon,
      impression_tracker: product.OnViewBeacon,
      onBasketChangeBeacon: product.OnBasketChangeBeacon,
      onWishlistBeacon: product.OnWishlistBeacon,
      isCriteo: true,
      sponsored: true,
      product_id: product.ParentSKU,
      lineItemId: product.LineItemID,
      product: {
        ...this.defaultPLPProductTemplate,
        CustomerRating: Number(product.Rating === '-1' ? '0' : product.Rating),
        ID: product.ProductId,
        ImageURL: product.Image,
        Name: (() => {
          try {
            return decodeURIComponent(product.ProductName);
          } catch {
            return product.ProductName;
          }
        })(),
      },
    };
  }

  private getFormattedProductForCarousel(product: CriteoProduct): CertonaSchemeItem {
    return {
      ...this.defaultCertonaTemplate,
      click_tracker: product.OnClickBeacon,
      impression_tracker: product.OnViewBeacon,
      CustomerRating: product.Rating,
      ID: product.ProductId,
      ImageURL: product.Image,
      Name: (() => {
        try {
          return decodeURIComponent(product.ProductName);
        } catch (error) {
          this.logger.error('Failed to decode URI: ', error);
          this.logErrorToHoneycomb({
            name: 'Error formatting Criteo Product for Carousel',
            message: error?.message || 'Error formatting Criteo Product for Carousel',
            method: 'getFormattedProductForCarousel',
            metadata: {
              productId: product.ProductId,
            },
          });
          return product.ProductName;
        }
      })(),
      SKUID: product.ParentSKU,
      URL: this.fallbackSeoUrlService.buildSeoUrl(product.ProductName, product.ParentSKU),
    };
  }

  // PLP Functions:
  public fireInGridOnLoadBeacon(beaconURL: string, excludedProducts = ''): void {
    if (!beaconURL || beaconURL === this.lastInGridOnLoadBeacon) {
      return;
    }
    this.fireBeacon(beaconURL, excludedProducts);
    this.lastInGridOnLoadBeacon = beaconURL;
  }

  public fireDSTOnLoadBeacon(beaconURL: string): void {
    if (!beaconURL || beaconURL === this.lastDSTOnLoadBeacon) {
      return;
    }
    this.fireBeacon(beaconURL);
    this.lastDSTOnLoadBeacon = beaconURL;
  }

  public isSponsoredProduct(
    sponsoredProducts: PlpInGridSponsoredProduct[] | null,
    productCardCount: number,
    pageNumber: number,
    filters: Record<string, string[]>,
    featureFlags: Record<string, boolean>,
    pinnedContent,
  ): boolean {
    // Ensure there are sponsored products available to place
    // Also ensure that if the product isn't a criteo product and we are either filtering or not on the first page, return false.
    if (
      !sponsoredProducts?.length ||
      (!sponsoredProducts[0]?.isCriteo && (pageNumber !== 0 || Object.keys(filters).length !== 0))
    ) {
      return false;
    }

    // Only run this if we are using the sponsored shelf and if we haven't checked yet
    if (featureFlags.isSPAShelfEnabled && !this.shelfChecked) {
      for (let i = 0; i < 4; i++) {
        if (typeof pinnedContent?.[i] !== 'undefined') {
          this.pinnedContentInFirstRow++;
        }
      }
      this.sponsoredShelfPositions = this.sponsoredShelfPositions.map(
        (pos) => pos - this.pinnedContentInFirstRow,
      );
      this.everyThirdSlot = this.sponsoredShelfPositions[3] % 3;
      this.shelfChecked = true;
    }

    // If the shelf is enabled and the pCC is equal to one of the shelf position numbers, return true.
    // If the second position is enabled and we are at the second position (pCC is equal to 1), return true.
    // If we are past the 7th position (pCC >= 6), check for every third position (calculated above). If we are at a third position, return true.
    // Otherwise, return false.
    return (
      (this.sponsoredShelfPositions.includes(productCardCount) && featureFlags.isSPAShelfEnabled) ||
      (productCardCount === 1 && featureFlags.isSPAPosition2Enabled) ||
      (productCardCount >= 6 && productCardCount % 3 === this.everyThirdSlot)
    );
  }

  /**
   * Creates CriteoViewItemMetadata from a ProductSku for pdp templates
   * @param productSku The first SKU from the product
   * @returns CriteoViewItemMetadata object
   */
  public createViewItemMetadata(productSku: ProductSku): CriteoViewItemMetadata {
    return {
      item: productSku?.partNumber,
      price: productSku?.prices?.offerPrice?.toString(),
      availability: productSku?.buyable,
      parentItem: productSku?.parentPartNumber,
    };
  }

  public checkOnLoadBeaconFiring(
    sponsoredSEO: string,
    beaconURL: string,
    sponProductCopy: PlpInGridSponsoredProduct[] | null,
    skippedIDs: string[],
  ): void {
    if (sponsoredSEO === 'N') {
      return;
    }

    if (sponProductCopy?.length) {
      for (const skipped of sponProductCopy) {
        skippedIDs.push(skipped.product.ID);
      }
    }

    const excludedProductsString = skippedIDs.join('|');
    this.fireInGridOnLoadBeacon(beaconURL, excludedProductsString);
  }

  public transformToPinnedContent(dst: CriteoDigitalShelfTalker): CriteoPinnedContentSpot | null {
    const dstr: CriteoDigitalShelfTalkerRendering = dst.rendering;
    const missingAttributes = CRITEO_PINNED_CONTENT_REQUIRED_ATTRIBUTES.filter(
      (attr) => !dstr[attr] || dstr[attr].trim() === '',
    );

    // Return null if any attributes are missing to prevent incomplete pinned content
    if (missingAttributes.length > 0) {
      // Only log warning if some (but not all) attributes are present
      if (missingAttributes.length < CRITEO_PINNED_CONTENT_REQUIRED_ATTRIBUTES.length) {
        this.logger.warn('Missing required attributes from Criteo to create PinnedContent:', dstr);
      }
      return null;
    }

    return {
      altText: dstr.header_text,
      csid: dst.LineItemID,
      cta: [{ link: dstr.redirect_url, linkTitle: dstr.button_text }],
      headline: dstr.header_text,
      onClickBeacon: dst.OnClickBeacon,
      onViewBeacon: dst.OnViewBeacon,
      pinnedPosition: CRITEO_DIGITAL_SHELF_TALKER_PINNED_POSITION,
      Subhead: dstr.subcopy_text,
      textColor: dstr.text_color?.toLowerCase(),
      visualAssets: [
        [
          {
            altText: dstr.header_text,
            type: 'imageUpload',
            value: dstr.background_image,
          },
        ],
      ],
    };
  }

  /**
   * Creates an IntersectionObserver for a single element that will only fire its callback after waiting
   * the specified time without receiving an update that the element is no longer in view.
   *
   * @param element The element to be observed.
   * @param callback The callback function to be executed when the element has been in view for the specified time.
   * @param threshold Intersection threshold at which to trigger the observer. Default is 0.5 (50% visibility).
   * @param timeInView Time in milliseconds the element must remain in view before the callback is executed. Default is 1000ms.
   *
   * @remarks
   * **Important**: Call {@link clearObserverTimers} in your component's `ngOnDestroy()`, `DestroyRef` callback, or 'beforeunload' window event
   * to clear all pending timers and prevent beacons from firing after the component is destroyed.
   */
  public sponsoredProductsIntersectionObserver(
    element: HTMLElement,
    callBack: () => void,
    threshold = 0.5,
    timeInView = 1000,
  ): void {
    let timerId: number | null = null;

    const observer = new IntersectionObserver(
      (entries) => {
        // multiple entries indicate the same product scrolling in and out of intersection quickly
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (timerId === null) {
              // element entered viewport - start timer
              timerId = window.setTimeout(() => {
                callBack(); // fire beacon
                observer.unobserve(element);
                // Remove timerId from active set after callback execution
                this.observerActiveTimerIds.delete(timerId);
              }, timeInView);

              // Add timerId to active set to cleanup if component is destroyed before timer fires
              this.observerActiveTimerIds.add(timerId);
            }
          } else {
            if (timerId === null) {
              // page load - intersection callback is fired when initialized
              return;
            }
            // element left viewport - cancel timer and reset timerId for next intersection
            clearTimeout(timerId);
            this.observerActiveTimerIds.delete(timerId);
            timerId = null;
          }
        });
      },
      { threshold },
    );

    observer.observe(element);
  }

  /**
   * Clears all pending timers created by {@link sponsoredProductsIntersectionObserver} to prevent
   * beacons from firing after the component is destroyed.
   */
  public clearObserverTimers(): void {
    this.observerActiveTimerIds.forEach((timerId) => {
      clearTimeout(timerId);
    });
    this.observerActiveTimerIds.clear();
  }
}
